/** One itemized backup line under a parsed deduction — a product, store or claim row. */
export interface AiParsedBackupLine {
  description?: string | null
  /** The line's amount, signed as the document shows it. */
  amount?: number | null
  quantity?: number | null
  unit_amount?: number | null
  upc?: string | null
  product_number?: string | null
  promo_type?: string | null
  retailer_name?: string | null
  store_number?: string | null
  po_number?: string | null
}

export interface AiParsedDeduction {
  remote_id: string
  invoice_number: string
  /** Signed as the document shows it. On an invoice line, the cash actually paid (net), not the gross. */
  invoice_amount: number
  /** Invoice lines only: the gross / original invoice figure before any early-pay discount. */
  gross_amount?: number | null
  /** Invoice lines only: the early-pay (terms) discount taken at payment time, positive. */
  discount_amount?: number | null
  source: string
  original_source?: string | null
  kind: string
  check_number?: string | null
  check_amount?: number | null
  /** When the payment was made. Falls back to the document-level check date on import. */
  check_date?: string | null
  po_number?: string | null
  execution_date?: string | null
  description?: string | null
  retailer_name?: string | null
  invoice_date?: string | null
  pending?: boolean
  /** Proposed backup detail lines for this deduction; editable in review, saved as backup rows. */
  backup_lines?: AiParsedBackupLine[]
}

export interface AiParsedResult {
  file_type: string
  check_number?: string | null
  check_date?: string | null
  check_amount?: number | null
  deductions: AiParsedDeduction[]
}

export interface AiParseResponse {
  s3_uri: string
  deduction_method: string
  parsed: AiParsedResult
  /** Set when the parsed lines don't add up to the payment total printed on the document. */
  warning?: string | null
}

export interface ConfirmAiImportRequest {
  /** The uploaded document; null for a check entered by hand. */
  s3_uri: string | null
  deduction_method: string
  parsed: AiParsedResult
}

export interface CreatedDeductionSummary {
  id: string
  invoice_number: string
  invoice_amount: number
  /** Early-pay discount booked on an invoice line, when one was. */
  discount_amount?: number | null
  source: string
  original_source?: string | null
  kind: string
  description?: string | null
  execution_date?: string | null
  check_number?: string | null
}

export interface SkippedDeductionSummary {
  invoice_number: string
  remote_id: string
  reason: string
}

export interface ConfirmAiImportResponse {
  created: CreatedDeductionSummary[]
  skipped: SkippedDeductionSummary[]
  count: number
}

export async function aiImportParse(file: File, deductionMethod: string): Promise<AiParseResponse> {
  const formData = new FormData()
  formData.append("file", file)
  formData.append("deduction_method", deductionMethod)

  const res = await fetch("/api/ai-import/parse", {
    method: "POST",
    body: formData,
    credentials: "include",
  })

  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || "Failed to parse document")
  }

  return res.json() as Promise<AiParseResponse>
}

/**
 * Parse an attachment that is already in S3 (an email attachment), rather than uploading a copy.
 *
 * The backend reuses the attachment's own `s3_uri`, so a confirmed import links the deduction to the
 * real email attachment. It refuses by default when deductions already point at the file; pass
 * `force` to parse it again anyway.
 */
export async function aiImportParseAttachment(
  attachment_id: string,
  deduction_method: string,
  force = false,
): Promise<AiParseResponse> {
  const res = await fetch("/api/ai-import/parse-attachment", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ attachment_id, deduction_method, force }),
    credentials: "include",
  })

  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || "Failed to parse attachment")
  }

  return res.json() as Promise<AiParseResponse>
}

export interface AiImportHistoryItem {
  id: string
  invoice_number: string
  invoice_amount: number
  source: string
  original_source?: string | null
  kind: string
  pending: boolean
  created_at: string
  description?: string | null
  check_number?: string | null
  execution_date?: string | null
  custom_deduction_method?: string | null
}

export async function aiImportHistory(): Promise<AiImportHistoryItem[]> {
  const res = await fetch("/api/ai-import/history", {
    credentials: "include",
  })

  if (!res.ok) {
    throw new Error("Failed to fetch AI import history")
  }

  return res.json() as Promise<AiImportHistoryItem[]>
}

export async function aiImportConfirm(req: ConfirmAiImportRequest): Promise<ConfirmAiImportResponse> {
  const res = await fetch("/api/ai-import/confirm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
    credentials: "include",
  })

  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || "Failed to confirm import")
  }

  return res.json() as Promise<ConfirmAiImportResponse>
}
